import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { axiosInstance } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { toast } from 'sonner';
import { Bell, RefreshCw, ChevronDown, ChevronUp, BookOpen, ArrowUpDown, Check, X, Eye } from 'lucide-react';
import PulseEntryDrilldownModal from '@/components/PulseEntryDrilldownModal';

export default function AlertsPage() {
  const navigate = useNavigate();
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterSeverity, setFilterSeverity] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [filterGym, setFilterGym] = useState('');
  const [referenceOpen, setReferenceOpen] = useState(false);
  const [selectedAlerts, setSelectedAlerts] = useState(new Set());
  const [sortConfig, setSortConfig] = useState({ key: 'severity', direction: 'desc' });
  const [drilldownEntryId, setDrilldownEntryId] = useState(null);
  const [drilldownOpen, setDrilldownOpen] = useState(false);

  // Navigation mapping for alert types
  const alertNavigation = {
    'Low Connect Rate': '/historical',
    'Connect Warning': '/historical',
    'Low Closure Rate': '/historical',
    'No Pulse Entry': '/pulse-entry',
    'Missing Pulse Data': '/pulse-entry',
    'Mgr Pending': '/verification',
    'Manager Pending': '/verification',
    'Target Gap': '/',
    'Below Target': '/',
    'Below Daily Target': '/',
    'Verification Late': '/verification',
    'Entry Rejected': '/pulse-entry',
    'Recurring Issue': '/alerts'
  };

  const handleAlertClick = (alert) => {
    const targetPage = alertNavigation[alert.alert_type] || '/';
    navigate(targetPage);
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get('/alerts', { params: { date } });
      setAlerts(response.data);
      setSelectedAlerts(new Set());
    } catch (error) {
      toast.error('Failed to load alerts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [date]);

  // Get unique alert types for filter dropdown
  const alertTypes = useMemo(() => {
    const types = [...new Set(alerts.map(a => a.alert_type))];
    return types.sort();
  }, [alerts]);

  // Filter and sort alerts
  const filteredAlerts = useMemo(() => {
    let result = alerts.filter(alert => {
      if (filterSeverity !== 'all') {
        if (filterSeverity === 'critical' && alert.severity !== '🔴') return false;
        if (filterSeverity === 'warning' && alert.severity !== '🟡') return false;
      }
      if (filterType !== 'all' && alert.alert_type !== filterType) return false;
      if (filterGym && !(alert.gym_name || alert.gym || '').toLowerCase().includes(filterGym.toLowerCase())) return false;
      return true;
    });

    // Sort
    if (sortConfig.key) {
      result.sort((a, b) => {
        let aVal = a[sortConfig.key] || '';
        let bVal = b[sortConfig.key] || '';

        // Special handling for severity (🔴 > 🟡)
        if (sortConfig.key === 'severity') {
          aVal = aVal === '🔴' ? 2 : aVal === '🟡' ? 1 : 0;
          bVal = bVal === '🔴' ? 2 : bVal === '🟡' ? 1 : 0;
        }

        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return result;
  }, [alerts, filterSeverity, filterType, filterGym, sortConfig]);

  // Count by severity
  const criticalCount = alerts.filter(a => a.severity === '🔴').length;
  const warningCount = alerts.filter(a => a.severity === '🟡').length;

  // Sort handler
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc'
    }));
  };

  // Selection handlers
  const toggleSelectAll = () => {
    if (selectedAlerts.size === filteredAlerts.length) {
      setSelectedAlerts(new Set());
    } else {
      setSelectedAlerts(new Set(filteredAlerts.map((_, idx) => idx)));
    }
  };

  const toggleSelect = (idx) => {
    const newSelected = new Set(selectedAlerts);
    if (newSelected.has(idx)) {
      newSelected.delete(idx);
    } else {
      newSelected.add(idx);
    }
    setSelectedAlerts(newSelected);
  };

  // Bulk actions
  const handleBulkAcknowledge = () => {
    const count = selectedAlerts.size;
    toast.success(`Acknowledged ${count} alert(s)`);
    setSelectedAlerts(new Set());
  };

  const handleBulkDismiss = () => {
    const count = selectedAlerts.size;
    toast.success(`Dismissed ${count} alert(s)`);
    setSelectedAlerts(new Set());
  };

  const SortHeader = ({ label, sortKey }) => (
    <th
      className="p-2 text-left font-bold border border-gray-300 cursor-pointer hover:bg-gray-100 select-none"
      onClick={() => handleSort(sortKey)}
    >
      <div className="flex items-center gap-1">
        {label}
        <ArrowUpDown size={14} className={`text-gray-400 ${sortConfig.key === sortKey ? 'text-blue-600' : ''}`} />
      </div>
    </th>
  );

  return (
    <div data-testid="alerts-page" className="max-w-full mx-auto">
      {/* Header */}
      <div className="mb-4">
        <h1 className="text-2xl font-bold text-[#1F4E79]">ALERTS & NOTIFICATIONS</h1>
        <p className="text-gray-600 text-sm">Critical issues and warnings requiring attention</p>
      </div>

      {/* Filters and Stats Row */}
      <Card className="mb-4">
        <CardContent className="p-3">
          <div className="flex flex-wrap gap-3 items-end justify-between">
            <div className="flex flex-wrap gap-3 items-end">
              <div className="space-y-1">
                <Label className="text-xs font-semibold">Date</Label>
                <Input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="w-36 h-9 border-gray-300"
                  data-testid="date-input"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-semibold">Severity</Label>
                <select
                  value={filterSeverity}
                  onChange={(e) => setFilterSeverity(e.target.value)}
                  className="h-9 px-2 border border-gray-300 rounded-md text-sm"
                >
                  <option value="all">All</option>
                  <option value="critical">🔴 Critical</option>
                  <option value="warning">🟡 Warning</option>
                </select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-semibold">Type</Label>
                <select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  className="h-9 px-2 border border-gray-300 rounded-md text-sm min-w-[140px]"
                >
                  <option value="all">All Types</option>
                  {alertTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-semibold">Gym</Label>
                <Input
                  type="text"
                  value={filterGym}
                  onChange={(e) => setFilterGym(e.target.value)}
                  placeholder="Search gym..."
                  className="w-36 h-9 border-gray-300"
                />
              </div>
              <Button onClick={fetchData} size="sm" className="h-9 bg-[#1F4E79] hover:bg-[#163a5c]">
                <RefreshCw className="w-4 h-4 mr-1" />
                Refresh
              </Button>
            </div>

            {/* Summary Stats */}
            <div className="flex gap-2">
              <div className="text-center px-3 py-1.5 bg-red-50 border border-red-200 rounded">
                <div className="text-xl font-bold text-red-600">{criticalCount}</div>
                <div className="text-xs text-gray-600">Critical</div>
              </div>
              <div className="text-center px-3 py-1.5 bg-yellow-50 border border-yellow-200 rounded">
                <div className="text-xl font-bold text-yellow-600">{warningCount}</div>
                <div className="text-xs text-gray-600">Warning</div>
              </div>
              <div className="text-center px-3 py-1.5 bg-gray-50 border border-gray-200 rounded">
                <div className="text-xl font-bold text-gray-600">{alerts.length}</div>
                <div className="text-xs text-gray-600">Total</div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Bulk Actions */}
      {selectedAlerts.size > 0 && (
        <div className="mb-3 p-2 bg-blue-50 border border-blue-200 rounded flex items-center gap-3">
          <span className="text-sm font-medium text-blue-700">{selectedAlerts.size} selected</span>
          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={handleBulkAcknowledge}>
            <Check size={14} className="mr-1" />
            Acknowledge
          </Button>
          <Button size="sm" variant="outline" className="h-7 text-xs text-red-600 border-red-200 hover:bg-red-50" onClick={handleBulkDismiss}>
            <X size={14} className="mr-1" />
            Dismiss
          </Button>
        </div>
      )}

      {/* Alerts Table */}
      <Card className="mb-4">
        <CardHeader className="bg-[#1F4E79] text-white py-2 px-3">
          <CardTitle className="text-base font-bold flex items-center gap-2">
            <Bell className="w-4 h-4" />
            ALERTS ({filteredAlerts.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
            </div>
          ) : filteredAlerts.length === 0 ? (
            <div className="p-8 text-center">
              <div className="text-4xl mb-2">✅</div>
              <h3 className="text-lg font-semibold text-gray-900 mb-1">All Clear!</h3>
              <p className="text-gray-600 text-sm">No alerts for selected filters</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[#D6DCE5]">
                    <th className="p-2 border border-gray-300 w-10">
                      <Checkbox
                        checked={selectedAlerts.size === filteredAlerts.length && filteredAlerts.length > 0}
                        onCheckedChange={toggleSelectAll}
                      />
                    </th>
                    <SortHeader label="Severity" sortKey="severity" />
                    <SortHeader label="Date" sortKey="date" />
                    <SortHeader label="Gym" sortKey="gym_name" />
                    <SortHeader label="TL" sortKey="tl_name" />
                    <SortHeader label="Pulse" sortKey="pulse_name" />
                    <SortHeader label="Source" sortKey="source_name" />
                    <SortHeader label="Type" sortKey="alert_type" />
                    <th className="p-2 text-left font-bold border border-gray-300">Issue</th>
                    <th className="p-2 text-left font-bold border border-gray-300">Value</th>
                    <th className="p-2 text-left font-bold border border-gray-300">Action</th>
                    <th className="p-2 text-center font-bold border border-gray-300 w-16">View</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAlerts.map((alert, idx) => {
                    const isCritical = alert.severity === '🔴';
                    return (
                      <tr
                        key={idx}
                        className={`${isCritical ? 'bg-red-50' : 'bg-yellow-50'} hover:bg-opacity-80 border-b`}
                        data-testid={`alert-row-${idx}`}
                      >
                        <td className="p-2 border border-gray-200 text-center">
                          <Checkbox
                            checked={selectedAlerts.has(idx)}
                            onCheckedChange={() => toggleSelect(idx)}
                          />
                        </td>
                        <td className="p-2 border border-gray-200 text-center text-xl">{alert.severity}</td>
                        <td className="p-2 border border-gray-200 text-gray-600 whitespace-nowrap">{alert.date}</td>
                        <td className="p-2 border border-gray-200 font-semibold">{alert.gym_name || alert.gym || '-'}</td>
                        <td className="p-2 border border-gray-200 text-gray-600">{alert.tl_name || alert.tl || '-'}</td>
                        <td className="p-2 border border-gray-200 text-gray-600">{alert.pulse_name || '-'}</td>
                        <td className="p-2 border border-gray-200 text-gray-600">{alert.source_name || '-'}</td>
                        <td className="p-2 border border-gray-200">
                          <span className={`px-1.5 py-0.5 rounded text-xs font-semibold ${
                            isCritical ? 'bg-red-200 text-red-800' : 'bg-yellow-200 text-yellow-800'
                          }`}>
                            {alert.alert_type}
                          </span>
                        </td>
                        <td className="p-2 border border-gray-200 text-gray-700 max-w-[200px] truncate" title={alert.issue}>
                          {alert.issue}
                        </td>
                        <td className="p-2 border border-gray-200 font-semibold text-gray-900">{alert.value}</td>
                        <td className="p-2 border border-gray-200 text-gray-600 text-xs max-w-[120px] truncate" title={alert.action}>
                          {alert.action}
                        </td>
                        <td className="p-2 border border-gray-200 text-center">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-6 w-6 p-0"
                            onClick={() => {
                              if (alert.pulse_entry_id) {
                                setDrilldownEntryId(alert.pulse_entry_id);
                                setDrilldownOpen(true);
                              } else {
                                // Fallback to navigation for alerts without pulse_entry_id (legacy)
                                handleAlertClick(alert);
                              }
                            }}
                            title="View pulse entry"
                          >
                            <Eye size={14} className="text-blue-600" />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Reference Guide - Collapsible */}
      <Collapsible open={referenceOpen} onOpenChange={setReferenceOpen}>
        <Card>
          <CollapsibleTrigger asChild>
            <CardHeader className="bg-slate-100 py-2 cursor-pointer hover:bg-slate-200 transition-colors">
              <CardTitle className="text-sm font-bold flex items-center justify-between text-slate-700">
                <div className="flex items-center gap-2">
                  <BookOpen className="w-4 h-4" />
                  Reference Guide
                </div>
                {referenceOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </CardTitle>
            </CardHeader>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <CardContent className="p-3 space-y-4 text-sm">
              {/* Alert Types */}
              <div>
                <h3 className="text-xs font-bold text-[#1F4E79] mb-2 uppercase">Alert Types</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                  <div className="p-2 bg-red-50 border border-red-200 rounded text-xs">
                    <span className="font-semibold">Low Connect</span>: Connect Rate &lt; 60%
                  </div>
                  <div className="p-2 bg-yellow-50 border border-yellow-200 rounded text-xs">
                    <span className="font-semibold">Connect Warning</span>: Connect Rate 60-70%
                  </div>
                  <div className="p-2 bg-red-50 border border-red-200 rounded text-xs">
                    <span className="font-semibold">No Pulse Entry</span>: Pulse time passed, no entry
                  </div>
                  <div className="p-2 bg-yellow-50 border border-yellow-200 rounded text-xs">
                    <span className="font-semibold">Mgr Pending</span>: TL verified, Mgr not within 4hrs
                  </div>
                  <div className="p-2 bg-yellow-50 border border-yellow-200 rounded text-xs">
                    <span className="font-semibold">Target Gap</span>: Daily closure &lt; 80% of target
                  </div>
                  <div className="p-2 bg-yellow-50 border border-yellow-200 rounded text-xs">
                    <span className="font-semibold">Verification Late</span>: Entry not TL verified in 2hrs
                  </div>
                </div>
              </div>

              {/* Severity Legend */}
              <div>
                <h3 className="text-xs font-bold text-[#1F4E79] mb-2 uppercase">Severity Levels</h3>
                <div className="flex gap-4">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">🔴</span>
                    <span className="text-xs"><strong>Critical:</strong> Immediate action</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-lg">🟡</span>
                    <span className="text-xs"><strong>Warning:</strong> Monitor and review</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>

      {/* Pulse Entry Drilldown Modal */}
      <PulseEntryDrilldownModal
        entryId={drilldownEntryId}
        open={drilldownOpen}
        onClose={() => setDrilldownOpen(false)}
      />
    </div>
  );
}
